import { workforceAccessToken, workforceSiteId } from "../api/workforceRequestScope";

const browserOrigin = () => globalThis.location?.origin ?? "http://127.0.0.1";

/** Prototype APIs live at the origin root, just like the encounter copilot lane. */
export function demoApiOrigin(): string {
  const configured = import.meta.env.VITE_AGENT_API_ORIGIN;
  if (typeof configured !== "string" || configured.length === 0) {
    if (import.meta.env.VITE_HOSTED_STAGING === "true")
      throw new Error("Hosted demo requests must use the configured staging API");
    return "http://127.0.0.1:4100";
  }
  return new URL(configured, browserOrigin()).origin;
}

/**
 * Fetch for the synthetic note-template and scheduling prototypes under
 * `/v1/demo/`. The encounter copilot does not use it.
 */
export async function demoFetch(url: string, init?: RequestInit): Promise<Response> {
  if (import.meta.env.VITE_HOSTED_STAGING !== "true") return localDemoFetch(url, init);
  const destination = new URL(url, browserOrigin());
  if (
    destination.origin !== demoApiOrigin() ||
    !destination.pathname.startsWith("/v1/demo/") ||
    destination.username ||
    destination.password
  ) {
    throw new Error("Hosted demo requests must use the configured staging API");
  }
  const accessToken = workforceAccessToken();
  if (accessToken === undefined) throw new Error("An active workforce session is required");
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${await accessToken()}`);
  const siteId = workforceSiteId();
  if (siteId) headers.set("X-Site-Id", siteId);
  else headers.delete("X-Site-Id");
  return fetch(destination.href, { ...init, headers, redirect: "error" });
}

/**
 * Local development works without a session. With one, requests to the configured API's demo
 * routes carry it. The token never goes anywhere else, and a session that cannot give one is
 * skipped.
 */
async function localDemoFetch(url: string, init?: RequestInit): Promise<Response> {
  const origin = import.meta.env.VITE_AGENT_API_ORIGIN;
  const accessToken = workforceAccessToken();
  if (accessToken === undefined || !origin) return fetch(url, init);
  let destination: URL;
  let configuredOrigin: string;
  try {
    destination = new URL(url, browserOrigin());
    configuredOrigin = demoApiOrigin();
  } catch {
    return fetch(url, init);
  }
  if (
    destination.origin !== configuredOrigin ||
    !destination.pathname.startsWith("/v1/demo/") ||
    destination.username ||
    destination.password
  )
    return fetch(url, init);
  let token: string;
  try {
    token = await accessToken();
  } catch {
    return fetch(url, init);
  }
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  const siteId = workforceSiteId();
  if (siteId) headers.set("X-Site-Id", siteId);
  return fetch(destination.href, { ...init, headers, redirect: "error" });
}
